// SVG bar chart used by the AI Usage and AI Cost and Usage pages.
// Drawn at the container's real pixel width (re-measured on resize), so text
// and bars are never stretched. The same file is used in dgtasks_vue, pmt_app
// and ggb_app: keep the copies identical.
//
// bars = [{ key, label, value, display, tooltip, highlight, segments? }]
// segments (optional) = [{ value, color }] draws a stacked bar summing to value.

const FONT = 11;
const CHAR_W = 6.4; // approx. px per character at FONT size

// Round a raw step up to 1 / 2 / 2.5 / 5 x 10^n so the axis reads cleanly.
function niceStep(raw) {
    if (!(raw > 0)) return 1;
    const pow = Math.pow(10, Math.floor(Math.log10(raw)));
    const f = raw / pow;
    const nice = f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10;
    return nice * pow;
}

function fmtAxis(v, step) {
    if (v === 0) return '$0';
    const decimals = step >= 1 ? 0 : step >= 0.1 ? 1 : step >= 0.01 ? 2 : 3;
    return '$' + v.toFixed(decimals);
}

export default {
    name: 'BarChart',
    props: {
        bars: { type: Array, default: () => [] },
        height: { type: Number, default: 170 },
        color: { type: String, default: '#5C6BC0' },
        compact: { type: Boolean, default: false },
    },
    data() {
        return { width: 0 };
    },
    mounted() {
        this.measure();
        if (typeof ResizeObserver !== 'undefined') {
            this._ro = new ResizeObserver(() => this.measure());
            this._ro.observe(this.$el);
        } else {
            window.addEventListener('resize', this.measure);
        }
    },
    beforeDestroy() {
        if (this._ro) this._ro.disconnect();
        window.removeEventListener('resize', this.measure);
    },
    methods: {
        measure() {
            const w = this.$el && this.$el.clientWidth ? Math.floor(this.$el.clientWidth) : 0;
            if (w && w !== this.width) this.width = w;
        },
    },
    render(h) {
        const W = this.width || 640;
        const H = this.height;
        const n = this.bars.length || 1;
        const topPad = 18; // room for value labels above the tallest bar
        const bottomPad = 24; // x-axis labels
        const plotTop = topPad;
        const plotBottom = H - bottomPad;
        const plotH = plotBottom - plotTop;

        // Axis scale on round numbers
        const rawMax = Math.max(...this.bars.map(b => Number(b.value) || 0), 0);
        const step = niceStep((rawMax || 1) / 4);
        const ticks = [];
        for (let v = 0; v <= (rawMax || 1) + step * 0.999; v += step) ticks.push(Math.round(v * 1e6) / 1e6);
        const axisMax = ticks[ticks.length - 1] || 1;
        const yOf = v => plotBottom - (v / axisMax) * plotH;

        const labels = ticks.map(t => fmtAxis(t, step));
        const G = Math.max(...labels.map(l => l.length)) * CHAR_W + 14; // left gutter
        const plotW = Math.max(W - G - 6, 20);
        const slot = plotW / n;
        const bw = Math.min(Math.max(slot * 0.64, 2), 56);

        const kids = [];

        // Gridlines + price axis
        ticks.forEach((t, i) => {
            const y = yOf(t);
            kids.push(h('line', { attrs: { x1: G, y1: y, x2: G + plotW, y2: y, stroke: i === 0 ? '#CFCFCF' : '#EEEEEE', 'stroke-width': 1 } }));
            kids.push(h('text', { attrs: { x: G - 8, y: y + 4, 'text-anchor': 'end', 'font-size': FONT, fill: '#888' } }, labels[i]));
        });

        // Which x labels and value labels fit without colliding
        const maxLabelLen = Math.max(...this.bars.map(b => String(b.label == null ? '' : b.label).length), 1);
        const labelEvery = Math.max(1, Math.ceil((maxLabelLen * CHAR_W + 10) / slot));
        const maxDisplayLen = Math.max(...this.bars.map(b => String(b.display == null ? '' : b.display).length), 1);
        const showValues = slot >= maxDisplayLen * CHAR_W + 6 && (!this.compact || n <= 16);

        this.bars.forEach((b, i) => {
            const value = Number(b.value) || 0;
            const cx = G + i * slot + slot / 2;
            const x = cx - bw / 2;
            const bh = value > 0 ? Math.max(2, (value / axisMax) * plotH) : 0;
            const top = plotBottom - bh;
            const tipText = b.tooltip || '';

            // Full-height transparent hit area so every day (even $0) has a tooltip
            kids.push(h('rect', { attrs: { x: G + i * slot, y: plotTop, width: slot, height: plotH + bottomPad, fill: 'transparent' } }, [h('title', tipText)]));

            if (value > 0 && Array.isArray(b.segments) && b.segments.length) {
                let y = plotBottom;
                b.segments.forEach(seg => {
                    const sh = (seg.value / value) * bh;
                    if (sh <= 0) return;
                    y -= sh;
                    kids.push(h('rect', { attrs: { x, y, width: bw, height: sh, fill: seg.color }, style: { pointerEvents: 'none' } }));
                });
            } else if (value > 0) {
                kids.push(h('rect', {
                    attrs: { x, y: top, width: bw, height: bh, rx: Math.min(3, bw / 2), fill: b.highlight ? '#0D47A1' : this.color },
                    style: { pointerEvents: 'none' },
                }));
            } else {
                // Zero day: thin baseline tick so the day still reads as present
                kids.push(h('rect', { attrs: { x, y: plotBottom - 1.5, width: bw, height: 1.5, fill: '#E0E0E0' }, style: { pointerEvents: 'none' } }));
            }

            if (value > 0 && showValues) {
                kids.push(h('text', {
                    attrs: { x: cx, y: top - 4, 'text-anchor': 'middle', 'font-size': FONT, fill: '#555' },
                    style: { pointerEvents: 'none' },
                }, b.display));
            }
            if (i % labelEvery === 0 || b.highlight) {
                kids.push(h('text', {
                    attrs: {
                        x: cx, y: plotBottom + 16, 'text-anchor': 'middle', 'font-size': FONT + 1,
                        fill: b.highlight ? '#0D47A1' : '#888', 'font-weight': b.highlight ? 'bold' : 'normal',
                    },
                    style: { pointerEvents: 'none' },
                }, b.label));
            }
        });

        return h('div', { style: { width: '100%' } }, [
            h('svg', {
                attrs: { width: W, height: H, viewBox: `0 0 ${W} ${H}` },
                style: { display: 'block', overflow: 'visible', fontFamily: 'inherit' },
            }, kids),
        ]);
    },
};
